// ============================================================================
// QuoteService - Quote CRUD, Conversion, Print & Email Operations
// ============================================================================
// Replaces V1's svc-quotes.js. Handles all quote operations: get, addSkus,
// removeLines, copy, delete, convert-to-order, print, email, address loading,
// and payment terms. Used by quote detail, quote list, and dashboard widgets.
// ============================================================================

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataService } from './data.service';

/**
 * Core quote operations service. Replaces V1's svc-quotes.js.
 * Used by quote detail, dashboard quote widget, and quote list.
 */
@Injectable()
export class QuoteService {

  constructor(private dataSvc: DataService) {}

  get(quoteId: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'ZZNS_QuoteGet',
      returnas: 'BASE',
      quote_id: quoteId,
      openQuote: '1'
    }]).pipe(map(res => this.extract(res)));
  }

  addSkus(quoteId: string, skuText: string): Observable<any> {
    // Format: spaces→}}, newlines→|, collapse duplicates
    let data = skuText
      .replace(/[\t ]+/g, '}}')
      .replace(/}}{2,}/g, '}}')
      .replace(/\n+/g, '|')
      .replace(/\|{2,}/g, '|')
      .replace(/^[|}]+|[|}]+$/g, '');

    return this.dataSvc.dgGet([{
      action: 'ZZNS_QuoteSkusAdd',
      returnas: 'BASE',
      quote_id: quoteId,
      data
    }]).pipe(map(res => this.extract(res)));
  }

  removeLines(keys: string): Observable<any> {
    return this.dataSvc.dsSave([{
      table: 'qtl',
      type: 'delete',
      rows: keys
    }]);
  }

  setPONumber(ecomUpdateKey: string, poNumber: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'QuoteSetPONr',
      returnas: '',
      ecomupdatekey: ecomUpdateKey,
      po_number: poNumber
    }]);
  }

  copy(quoteId: string): Observable<string> {
    return this.dataSvc.dgGet([{
      action: 'ZZNS_QuoteCopy',
      returnas: 'BASE',
      quote_id: quoteId
    }]).pipe(
      map(res => {
        const data = this.extract(res);
        return data?.new_quote_id || data?.quote_id || '';
      })
    );
  }

  delete(ecomUpdateKey: string, quoteId: string): Observable<any> {
    return this.dataSvc.dsSave([{
      table: 'qt',
      type: 'upsert',
      rows: JSON.stringify([{
        EcomUpdateKey: ecomUpdateKey,
        status: -1,
        customer_status: 6
      }])
    }]);
  }

  convert(quoteId: string): Observable<any> {
    return this.dataSvc.getData([{
      action: 'ZZNS_QuoteConvert',
      returnas: 'BASE',
      quote_id: quoteId
    }]);
  }

  print(quoteId: string): Observable<string> {
    return this.dataSvc.getData([{
      action: 'QuotePrint',
      returnas: '',
      quote_id: quoteId
    }]);
  }

  email(quoteId: string, emailAddr: string): Observable<any> {
    return this.dataSvc.getData([{
      action: 'QuoteEmail',
      returnas: '',
      quote_id: quoteId,
      email: emailAddr
    }]);
  }

  saveSnapshot(quoteId: string, userId: string, source = 'Print'): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'QuoteSaveSnapshot',
      returnas: '',
      quoteid: quoteId,
      userid: userId,
      source,
      allowdupe: '0'
    }]);
  }

  changePmtTerms(quoteId: string, custId: string, ccPayment: number): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'ZZNS_PmtTermsChange',
      returnas: 'BASE',
      quote_id: quoteId,
      CustID: custId,
      cc_payment: ccPayment
    }]).pipe(map(res => this.extract(res)));
  }

  addImageItem(itemId: string, quoteId: string, plid: string, description: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'QuoteAddImageItem',
      returnas: 'BASE',
      itemid: itemId,
      quote_id: quoteId,
      plid,
      description,
      getquote: '1'
    }]).pipe(map(res => this.extract(res)));
  }

  private extract(res: any): any {
    let data = res;
    if (Array.isArray(data) && data[0]?.Data) {
      data = data[0].Data;
    }
    if (Array.isArray(data) && data.length === 1) {
      data = data[0];
    }
    // Unwrap named wrapper like { "Quote": { ... } }
    if (data && !Array.isArray(data) && typeof data === 'object') {
      const keys = Object.keys(data);
      if (keys.length === 1 && typeof data[keys[0]] === 'object' && !Array.isArray(data[keys[0]])) {
        data = data[keys[0]];
      }
    }
    return data;
  }
}
